import { ReactNode, useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface TooltipProps {
  content: string;
  children: ReactNode;
  className?: string;
}

export const Tooltip = ({ content, children, className }: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<'center' | 'left' | 'right'>('center');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const tooltipWidth = 200; // max-w-[200px]

      // Check if tooltip would overflow on the right
      if (rect.left + tooltipWidth / 2 > window.innerWidth - 10) {
        setPosition('right');
      }
      // Check if tooltip would overflow on the left
      else if (rect.left - tooltipWidth / 2 < 10) {
        setPosition('left');
      }
      else {
        setPosition('center');
      }
    }
  }, [isVisible]);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div
          className={cn(
            'absolute z-50 px-2 py-1 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg',
            'whitespace-nowrap pointer-events-none max-w-[200px]',
            'bottom-full mb-1',
            position === 'center' && 'left-1/2 -translate-x-1/2',
            position === 'right' && 'right-0',
            position === 'left' && 'left-0',
            className
          )}
        >
          {content}
          <div
            className={cn(
              'absolute top-full w-0 h-0 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700',
              position === 'center' && 'left-1/2 -translate-x-1/2',
              position === 'right' && 'right-2',
              position === 'left' && 'left-2'
            )}
          />
        </div>
      )}
    </div>
  );
};
